import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  GitCompare,
  Loader2,
  Plus,
  Minus,
  Edit3,
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { templateService } from '@/lib/api/services/templates'
import type { TemplateVersion, TemplateVersionComparison, VersionDifference } from '@/lib/api/types'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/components/ui/use-toast'

interface VersionComparisonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  versionId1: string
  versionId2: string
}

type ViewMode = 'unified' | 'split'

export function VersionComparisonDialog({
  open,
  onOpenChange,
  templateId,
  versionId1,
  versionId2,
}: VersionComparisonDialogProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [comparison, setComparison] = useState<TemplateVersionComparison | null>(null)
  const [version1, setVersion1] = useState<TemplateVersion | null>(null)
  const [version2, setVersion2] = useState<TemplateVersion | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('split')
  const [activeTab, setActiveTab] = useState<'content' | 'metadata' | 'variables'>('content')

  useEffect(() => {
    if (open && versionId1 && versionId2) {
      loadComparison()
    }
  }, [open, templateId, versionId1, versionId2])

  const loadComparison = async () => {
    try {
      setLoading(true)
      // Load comparison data and individual versions in parallel
      const [comparisonData, v1Data, v2Data] = await Promise.all([
        templateService.compareTemplateVersions(templateId, versionId1, versionId2).catch(() => null),
        templateService.getTemplateVersion(templateId, versionId1).catch(() => null),
        templateService.getTemplateVersion(templateId, versionId2).catch(() => null),
      ])

      setComparison(comparisonData)
      setVersion1(v1Data)
      setVersion2(v2Data)

      // If we couldn't load from API, create basic comparison from versions
      if (!comparisonData && v1Data && v2Data) {
        const generatedDiffs = generateDifferences(v1Data, v2Data)
        setComparison({
          version1: v1Data,
          version2: v2Data,
          differences: generatedDiffs,
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load version comparison',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  // Generate basic differences between two versions
  const generateDifferences = (v1: TemplateVersion, v2: TemplateVersion): VersionDifference[] => {
    const diffs: VersionDifference[] = []

    // Compare content
    if (v1.content !== v2.content) {
      diffs.push({
        field: 'content',
        oldValue: v1.content || '',
        newValue: v2.content || '',
        changeType: 'modified',
      })
    }

    // Compare variables
    const v1Vars = new Set(v1.variables?.map((v) => v.name) || [])
    const v2Vars = new Set(v2.variables?.map((v) => v.name) || [])

    v2.variables?.forEach((variable) => {
      if (!v1Vars.has(variable.name)) {
        diffs.push({
          field: `variable.${variable.name}`,
          oldValue: '',
          newValue: JSON.stringify(variable),
          changeType: 'added',
        })
      }
    })

    v1.variables?.forEach((variable) => {
      if (!v2Vars.has(variable.name)) {
        diffs.push({
          field: `variable.${variable.name}`,
          oldValue: JSON.stringify(variable),
          newValue: '',
          changeType: 'removed',
        })
      }
    })

    // Check for modified variables
    v1.variables?.forEach((v1Var) => {
      const v2Var = v2.variables?.find((v) => v.name === v1Var.name)
      if (v2Var && JSON.stringify(v1Var) !== JSON.stringify(v2Var)) {
        diffs.push({
          field: `variable.${v1Var.name}`,
          oldValue: JSON.stringify(v1Var),
          newValue: JSON.stringify(v2Var),
          changeType: 'modified',
        })
      }
    })

    return diffs
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Compute content diff lines
  const contentDiff = useMemo(() => {
    if (!version1 || !version2) return { lines1: [], lines2: [], unifiedLines: [] }

    const lines1 = (version1.content || '').split('\n')
    const lines2 = (version2.content || '').split('\n')

    // Simple line-by-line diff
    const maxLines = Math.max(lines1.length, lines2.length)
    const unifiedLines: Array<{
      type: 'unchanged' | 'added' | 'removed' | 'modified'
      line1?: string
      line2?: string
      lineNum1?: number
      lineNum2?: number
    }> = []

    for (let i = 0; i < maxLines; i++) {
      const l1 = lines1[i]
      const l2 = lines2[i]

      if (l1 === l2) {
        unifiedLines.push({ type: 'unchanged', line1: l1, line2: l2, lineNum1: i + 1, lineNum2: i + 1 })
      } else if (l1 === undefined) {
        unifiedLines.push({ type: 'added', line2: l2, lineNum2: i + 1 })
      } else if (l2 === undefined) {
        unifiedLines.push({ type: 'removed', line1: l1, lineNum1: i + 1 })
      } else {
        unifiedLines.push({ type: 'modified', line1: l1, line2: l2, lineNum1: i + 1, lineNum2: i + 1 })
      }
    }

    return { lines1, lines2, unifiedLines }
  }, [version1, version2])

  // Get stats for the comparison
  const stats = useMemo(() => {
    const diffs = comparison?.differences || []
    return {
      added: diffs.filter((d) => d.changeType === 'added').length,
      removed: diffs.filter((d) => d.changeType === 'removed').length,
      modified: diffs.filter((d) => d.changeType === 'modified').length,
    }
  }, [comparison])

  const getChangeIcon = (type: 'added' | 'removed' | 'modified') => {
    switch (type) {
      case 'added':
        return <Plus className="h-3.5 w-3.5 text-green-600" />
      case 'removed':
        return <Minus className="h-3.5 w-3.5 text-red-600" />
      case 'modified':
        return <Edit3 className="h-3.5 w-3.5 text-amber-600" />
    }
  }

  const getChangeColor = (type: 'added' | 'removed' | 'modified' | 'unchanged') => {
    switch (type) {
      case 'added':
        return 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500'
      case 'removed':
        return 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500'
      case 'modified':
        return 'bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500'
      default:
        return ''
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-600">
              <GitCompare className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>Version Comparison</DialogTitle>
              <DialogDescription>
                Comparing version {version1?.versionNumber || '...'} with version {version2?.versionNumber || '...'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading comparison...</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Stats Bar */}
            <div className="flex items-center justify-between py-3 px-1 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5 text-sm">
                  <Plus className="h-4 w-4 text-green-600" />
                  <span className="text-green-600 font-medium">{stats.added} added</span>
                </span>
                <span className="flex items-center gap-1.5 text-sm">
                  <Minus className="h-4 w-4 text-red-600" />
                  <span className="text-red-600 font-medium">{stats.removed} removed</span>
                </span>
                <span className="flex items-center gap-1.5 text-sm">
                  <Edit3 className="h-4 w-4 text-amber-600" />
                  <span className="text-amber-600 font-medium">{stats.modified} modified</span>
                </span>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('split')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                    viewMode === 'split'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  )}
                >
                  Split
                </button>
                <button
                  onClick={() => setViewMode('unified')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                    viewMode === 'unified'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  )}
                >
                  Unified
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 py-2 border-b border-gray-200 dark:border-gray-700">
              {(['content', 'metadata', 'variables'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize',
                    activeTab === tab
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <ScrollArea className="flex-1 mt-4">
              {activeTab === 'content' && (
                <div className="space-y-4">
                  {viewMode === 'split' ? (
                    <div className="grid grid-cols-2 gap-4">
                      {/* Version 1 */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <ChevronLeft className="h-4 w-4" />
                          <span className="font-medium">Version {version1?.versionNumber}</span>
                          {version1?.isCurrent && <Badge variant="success" className="text-xs">Current</Badge>}
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <pre className="p-4 text-xs font-mono bg-gray-50 dark:bg-gray-800/50 overflow-x-auto max-h-[400px]">
                            {contentDiff.unifiedLines.map((line, idx) => (
                              <div
                                key={idx}
                                className={cn(
                                  'flex',
                                  line.type === 'removed' && 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
                                  line.type === 'modified' && 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                )}
                              >
                                <span className="w-8 text-right pr-2 text-gray-400 select-none">
                                  {line.lineNum1 || ''}
                                </span>
                                <span className="flex-1">{line.line1 ?? ''}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      </div>

                      {/* Version 2 */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Version {version2?.versionNumber}</span>
                          {version2?.isCurrent && <Badge variant="success" className="text-xs">Current</Badge>}
                          <ChevronRight className="h-4 w-4" />
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <pre className="p-4 text-xs font-mono bg-gray-50 dark:bg-gray-800/50 overflow-x-auto max-h-[400px]">
                            {contentDiff.unifiedLines.map((line, idx) => (
                              <div
                                key={idx}
                                className={cn(
                                  'flex',
                                  line.type === 'added' && 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
                                  line.type === 'modified' && 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                )}
                              >
                                <span className="w-8 text-right pr-2 text-gray-400 select-none">
                                  {line.lineNum2 || ''}
                                </span>
                                <span className="flex-1">{line.line2 ?? ''}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Unified View */
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <pre className="p-4 text-xs font-mono bg-gray-50 dark:bg-gray-800/50 overflow-x-auto max-h-[500px]">
                        {contentDiff.unifiedLines.map((line, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex',
                              getChangeColor(line.type)
                            )}
                          >
                            <span className="w-8 text-right pr-2 text-gray-400 select-none">
                              {line.lineNum1 || line.lineNum2 || ''}
                            </span>
                            <span className="w-6 text-center select-none">
                              {line.type === 'added' && <Plus className="h-3 w-3 inline text-green-600" />}
                              {line.type === 'removed' && <Minus className="h-3 w-3 inline text-red-600" />}
                              {line.type === 'modified' && <Edit3 className="h-3 w-3 inline text-amber-600" />}
                            </span>
                            <span className="flex-1">
                              {line.type === 'removed' || line.type === 'unchanged' ? line.line1 : line.line2}
                            </span>
                          </div>
                        ))}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'metadata' && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Version 1 Metadata */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <ChevronLeft className="h-4 w-4" />
                      Version {version1?.versionNumber}
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <User className="h-3.5 w-3.5" />
                          Author
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {version1?.createdByName || version1?.createdBy}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Created
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {version1 && formatDate(version1.createdAt)}
                        </p>
                      </div>
                      {version1?.changeDescription && (
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Comment
                          </div>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {version1.changeDescription}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Version 2 Metadata */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      Version {version2?.versionNumber}
                      <ChevronRight className="h-4 w-4" />
                    </h4>
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <User className="h-3.5 w-3.5" />
                          Author
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {version2?.createdByName || version2?.createdBy}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Created
                        </div>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {version2 && formatDate(version2.createdAt)}
                        </p>
                      </div>
                      {version2?.changeDescription && (
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Comment
                          </div>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {version2.changeDescription}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'variables' && (
                <div className="space-y-4">
                  {comparison?.differences
                    .filter((d) => d.field.startsWith('variable.'))
                    .map((diff, idx) => {
                      const varName = diff.field.replace('variable.', '')
                      return (
                        <div
                          key={idx}
                          className={cn(
                            'p-4 rounded-lg border',
                            diff.changeType === 'added' && 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10',
                            diff.changeType === 'removed' && 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10',
                            diff.changeType === 'modified' && 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10'
                          )}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {getChangeIcon(diff.changeType)}
                            <span className="font-medium text-gray-900 dark:text-white">{varName}</span>
                            <Badge
                              variant={
                                diff.changeType === 'added'
                                  ? 'success'
                                  : diff.changeType === 'removed'
                                  ? 'destructive'
                                  : 'warning'
                              }
                              className="text-xs capitalize"
                            >
                              {diff.changeType}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-3">
                            {diff.oldValue && (
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Previous</p>
                                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">
                                  {JSON.stringify(JSON.parse(diff.oldValue), null, 2)}
                                </pre>
                              </div>
                            )}
                            {diff.newValue && (
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">New</p>
                                <pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">
                                  {JSON.stringify(JSON.parse(diff.newValue), null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}

                  {(!comparison?.differences || comparison.differences.filter((d) => d.field.startsWith('variable.')).length === 0) && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                      <p>No variable changes between these versions</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
